<template>
  <div @contextmenu.stop.prevent="showContextMenu" :style="tileStyle">
    <drop @click.native="showStayDialog()" @drop="handleStayDrop" @dragover="handleStayDragover(...arguments)">
      <v-layout row>
        <v-flex style="overflow:hidden;">
          <span :style="statusBarStyle"></span>
          <span class="clientText" :style="tileStyle">{{ clientText }}</span>
          <span v-if="alerts" v-for="(code, index) of alerts()" :key="index">
            <alert-visualized-coded-entry class="alert" :visualized-coded-entry="code" :alertDisplayName="getAlertDisplayName(code)"></alert-visualized-coded-entry>
          </span>
        </v-flex>
        <v-flex text-xs-right style="overflow:hidden;">
          <div class="attendingPhysicianName">{{attendingPhysicianName}}</div>
        </v-flex>
      </v-layout>
    </drop>

    <v-dialog v-if="isStayDialogVisible" v-model="isStayDialogVisible" scrollable min-height="95%" width="800px">
      <v-flex>
        <stay-card :unit="unit" :room="room" :stay="stayCopy" :isEditable="unit.isStayEditingAllowed" :onCancel="closeStayDialog" :onSave="saveStay"></stay-card>
      </v-flex>
    </v-dialog>

    <v-dialog v-if="isSwapDialogVisible" v-model="isSwapDialogVisible" scrollable height="300px" width="500px">
      <v-flex>
        <swap-confirmation :swapParams="swapParams" :isEditable="unit.isStayEditingAllowed" :onCancel="closeSwapDialog" :onSwap="swapStays"></swap-confirmation>
      </v-flex>
    </v-dialog>

    <v-menu v-model="isContextMenuVisible" :position-x="x" :position-y="y" absolute offset-y>
      <v-list>
        <v-list-tile @click="showStayDialog()">Edit Stay</v-list-tile>
        <v-list-tile @click="showRemoveStayDialog()" v-if="unit.isRemoveStayAllowed">Remove Stay</v-list-tile>
      </v-list>
    </v-menu>
  </div>
</template>

<script>
import moment from 'moment';

import appSettings from '../appSettings';

import AlertVisualizedCodedEntry from './AlertVisualizedCodedEntry.vue';
import StayCard from './StayCard.vue';
import SwapConfirmation from './SwapConfirmation.vue';
import bedBoardUtils from '../bedBoardUtils';

export default {
  name: 'StayTile',
  data() {
    return {
      isContextMenuVisible: false,
      isStayDialogVisible: false,
      isSwapDialogVisible: false,
      stayCopy: null,
      swapParams: null,
      x: 0,
      y: 0
    };
  },
  props: {
    room: Object,
    stay: Object,
    unit: Object
  },
  components: {
    'alert-visualized-coded-entry': AlertVisualizedCodedEntry,
    'stay-card': StayCard,
    'swap-confirmation': SwapConfirmation,
  },
  computed: {
    attendingPhysicianName() {
      let returnValue = null;
      if (
        this.stay
        && this.stay.clientSummary
        && this.stay.clientSummary.attendingPhysicianName
      ) {
        returnValue = this.stay.clientSummary.attendingPhysicianName.last;
      }
      return returnValue;
    },

    clientText() {
      let returnValue = null;
      if (this.stay.clientSummary && this.stay.clientSummary.clientName) {
        let age = '-';
        if (this.stay.clientSummary.birthDate) {
          age = moment().diff(this.stay.clientSummary.birthDate, 'years');
        }
        let gender = '-';
        if (this.stay.clientSummary.genderDescription) {
          gender = this.stay.clientSummary.genderDescription;
        }

        let actionableDescription = null;
        actionableDescription = this.isActionable
          ? this.stay.dischargeDescription
          : this.stay.reservationDescription;

        if (actionableDescription) {
          returnValue = `${
            this.stay.clientSummary.clientName.first
          } ${this.stay.clientSummary.clientName.last.charAt(0)}. (${age}, ${gender}, ${actionableDescription})`;
        } else {
          returnValue = `${
            this.stay.clientSummary.clientName.first
          } ${this.stay.clientSummary.clientName.last.charAt(0)}. (${age}, ${gender})`;
        }
      }
      return returnValue;
    },

    isActionable() {
      return this.$store.getters.isStayActionable(this.stay);
    },

    statusBarStyle() {
      let returnValue = null;
      let visualizedCodedEntry = null;
      if (this.isActionable) {
        if (this.stay && this.stay.dischargeStatusCode) {
          visualizedCodedEntry = this.$store.getters.getDischargeStatusCode(this.stay.dischargeStatusCode);
        }
      } else {
        visualizedCodedEntry = this.stay.reservationStatusCode;
      }

      if (visualizedCodedEntry) {
        returnValue = {
          borderColor: visualizedCodedEntry.backgroundColorHexValue
            ? visualizedCodedEntry.backgroundColorHexValue
            : '#ffffff',
          borderStyle: 'solid',
          borderLeftWidth: '2px',
          marginRight: '5px',
          verticalAlign: 'middle'
        };
      }

      return returnValue;
    },
    tileStyle() {
      let returnValue = null;

      if (this.stay.stayTypeCode) {
        returnValue = {
          background: this.stay.stayTypeCode.backgroundColorHexValue
            ? this.stay.stayTypeCode.backgroundColorHexValue
            : '#ffffff',
          color: this.stay.stayTypeCode.foregroundColorHexValue
            ? this.stay.stayTypeCode.foregroundColorHexValue
            : '#000000'
        };
      }

      return returnValue;
    }
  },
  created() {
    this.$eventbus.$on('CloseAllContextMenus', () => {
      this.isContextMenuVisible = false;
    });
  },
  methods: {
    alerts() {
      let returnValue = null;
      if (this.stay && this.stay.bedBoardAlerts) {
        returnValue = this.$store.getters.getStandardAssignedAlertCodes(this.stay.bedBoardAlerts);
      }
      return returnValue;
    },
    closeStayDialog() {
      this.stayCopy = null;
      this.isStayDialogVisible = false;
    },
    closeSwapDialog() {
      this.isSwapDialogVisible = false;
    },
    handleStayDragover(data, event) {
      if (!this.unit.isSwapStayAllowed || !this.isActionable || this.stay.bedBoardStayID.id === data.stayID.id) {
        event.dataTransfer.dropEffect = 'none';
      }
    },
    getAlertDisplayName(standardCode) {
      let returnValue = standardCode.displayName;
      const match = this.stay.bedBoardAlerts.find(w => w.code === standardCode.code);
      if (match && match.displayName) {
        returnValue = match.displayName;
      }
      return returnValue;
    },
    handleStayDrop(data, event) {
      event.stopPropagation();

      if (this.unit.isSwapStayAllowed && this.isActionable && this.stay.bedBoardStayID.id !== data.stayID.id) {
        if (data) {
          this.swapParams = {
            fromValues: data,
            withValues: {
              bedID: {
                id:
                this.stay.bedID && this.stay.bedID.id
                  ? this.stay.bedID.id
                  : null
              },
              bedName:
              this.stay.bedID
              && this.room.beds
              && this.room.beds.length > 0
              && this.room.beds.find(bed => bed.bedBoardBedID.id === this.stay.bedID.id)
                ? this.room.beds.find(bed => bed.bedBoardBedID.id === this.stay.bedID.id).name
                : null,
              beds: this.$store.getters.getAvailableBeds(
                this.room.bedBoardRoomID,
                this.stay.bedBoardStayID
              ),
              clientID: this.stay.clientSummary.clientID,
              clientName: this.stay.clientSummary.clientName,

              roomID: this.room.bedBoardRoomID,
              roomName: this.room.name,

              stayID: this.stay.bedBoardStayID,
              unitID: this.unit.bedBoardUnitID
            }
          };

          this.isSwapDialogVisible = true;
        }
      }
    },
    saveStay(changedStay) {
      const params = {
        roomID: this.room.bedBoardRoomID.id,
        stayID: changedStay.bedBoardStayID,
        values: {
          alerts: changedStay.bedBoardAlerts,
          bedID: changedStay.bedID,
          dischargeDescription: changedStay.dischargeDescription,
          dischargeStatusCode: changedStay.dischargeStatusCode,
          expectedDischargeDate: changedStay.expectedDischargeDate,
          reservationDescription: changedStay.reservationDescription,
          reservationStatusCode: changedStay.reservationStatusCode,
          staffNotes: changedStay.staffNotes,
          stayTypeCode: changedStay.stayTypeCode
        }
      };

      changedStay.auditInformation = {
        lastUpdatedByStaffMemberID: {
          scopeID: this.$store.getters.scopeID,
          id: this.$store.getters.userInfo.providerID
        },
        lastUpdatedDate: moment().format()
      };

      bedBoardUtils.raiseBedBoardStayUpdated(changedStay)
        .then(() => {
          bedBoardUtils.sendUpdateStayValuesSignal(this, params)
            .then(() => {})
            .catch((err) => {
              this.$log.error(`Error sending UpdateStayValues signal: ${err.message}`);
              this.$eventbus.$emit('showToast', { header: 'Error', message: 'Unable to send stay updated signal.', type: 'error' });
            });
          this.$store.commit('updateStayValues', params);
        })
        .catch((err) => {
          this.$log.error(`Error raising StayUpdated transaction: ${err.message}`);
          this.$eventbus.$emit('showToast', { header: 'Error', message: 'Unable to raise the stay updated event.', type: 'error' });
        });

      this.closeStayDialog();
    },

    showRemoveStayDialog() {
      this.$dialog
        .confirm(
          { title: 'Confirmation', body: 'Really remove this client/stay?' },
          {
            okText: 'Remove',
            cancelText: 'Cancel',
            animation: 'fade',
            backDropClose: true,
            reverse: true
          }
        )
        .then(() => {
          const params = {
            bedID: this.stay.bedID,
            clientID: this.stay.clientSummary.clientID,
            narrative: 'removed via BB Web App',
            roomID: this.room.bedBoardRoomID,
            staffMemberProviderID: {
              scopeID: this.$store.getters.scopeID,
              id: this.$store.getters.userInfo.providerID
            },
            stayID: this.stay.bedBoardStayID,
            unitID: this.unit.bedBoardUnitID
          };

          bedBoardUtils.raiseBedBoardRemoveStayTransactionCreated(params)
            .then(() => {
              bedBoardUtils.sendRemoveStaySignal(this, params)
                .then(() => {})
                .catch((err) => {
                  this.$log.error(`Error sending RemoveStay signal: ${err.message}`);
                  this.$eventbus.$emit('showToast', { header: 'Error', message: 'Unable to send stay removed signal.', type: 'error' });
                });
              this.$store.commit('removeStay', params);
            })
            .catch((err) => {
              this.$log.error(`Error raising RoomUpdated transaction: ${err.message}`);
              this.$eventbus.$emit('showToast', { header: 'Error', message: 'Unable to raise the stay removed event.', type: 'error' });
            });
        })
        .catch(() => {});
    },

    showContextMenu(e) {
      if (this.isActionable) {
        this.$eventbus.$emit('CloseAllContextMenus');

        e.preventDefault();
        this.x = e.clientX;
        this.y = e.clientY;
        this.isContextMenuVisible = false;

        this.$nextTick(() => {
          this.isContextMenuVisible = true;
        });
      }
    },

    showStayDialog() {
      this.stayCopy = bedBoardUtils.clone(this.stay);
      if (!this.stayCopy.bedBoardAlerts) {
        this.stayCopy.bedBoardAlerts = [];
        this.stayCopy.bedBoardAlerts.push({});
        this.stayCopy.bedBoardAlerts.splice(0, 1);
      }
      this.isStayDialogVisible = true;
    },

    swapStays(params) {
      this.isSwapDialogVisible = false;
      bedBoardUtils.raiseBedBoardSwapStayTransactionCreated(params)
        .then(() => {
          bedBoardUtils.sendSwapStaysSignal(this, params)
            .then(() => {})
            .catch((err) => {
              this.$log.error(`Error sending SwapStay signal: ${err.message}`);
              this.$eventbus.$emit('showToast', { header: 'Error', message: 'Unable to send swap stay signal.', type: 'error' });
            });
          this.$store.commit('swapStays', params);
        })
        .catch((err) => {
          this.$log.debug(`[${moment().format(appSettings.logDateFormat)}] Error raising SwapStay transaction: ${err.message}`);
          this.$eventbus.$emit('showToast', { header: 'Error', message: 'Unable to raise the swap stay event.', type: 'error' });
        });
    }
  }
};
</script>

<style lang="scss">
.alert {
  margin-left: 5px;
  vertical-align: middle;
}
.attendingPhysicianName {
  color: #000000;
  font-family: "Lato";
  font-size: 16px;
  font-style: italic;
  font-weight: 700;
  margin-right: 5px;
  overflow-wrap: break-word !important;
}
.clientText {
  color: #333333;
  font-family: "Lato";
  font-size: 14px;
  font-weight: 700;
  margin-right: 15px;
  overflow-wrap: break-word !important;
  vertical-align: middle;
}
</style>
